'use client';

import * as React from 'react';
import {createPortal} from 'react-dom';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@/matic/lib/utils';

// Dialog Root Component
type DialogProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
};

export function Dialog({open, onOpenChange, children}: DialogProps) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, []);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onOpenChange?.(false);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, onOpenChange]);

  if (!open || !mounted) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <div
        className="animate-in fade-in-0 fixed inset-0 bg-black/50 transition-opacity"
        onClick={() => onOpenChange?.(false)}
      />
      {children}
    </div>,
    document.body
  );
}

// Dialog Content Component
const dialogContentVariants = cva(
  'relative z-50 flex max-h-[85vh] w-full max-w-lg flex-col rounded-lg bg-white p-6 shadow-lg',
  {
    variants: {
      size: {
        sm: 'max-w-sm',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

type DialogContentProps = {} & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof dialogContentVariants>;

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  DialogContentProps
>(({className, size, ...props}, ref) => (
  <div
    ref={ref}
    className={cn(dialogContentVariants({size}), className)}
    role="dialog"
    aria-modal="true"
    {...props}
  />
));
DialogContent.displayName = 'DialogContent';

// Dialog Header Component
export function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('mb-4 shrink-0', className)} {...props} />;
}

// Dialog Title Component
export function DialogTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn('text-lg font-semibold text-gray-900', className)}
      {...props}
    />
  );
}

// Dialog Description Component
export function DialogDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-gray-600', className)} {...props} />;
}

// Dialog Footer Component
export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-6 flex shrink-0 justify-end space-x-2', className)}
      {...props}
    />
  );
}
